import { useCallback, useEffect, useState } from 'react';
import { messageFrom } from '@bp-files/client';
import { Badge } from '@bp-files/ui';
import { SurfaceHeader } from '@/surfaces/SurfaceHeader';
import { getScanners, type Scanners } from '@/lib/api';
import { SCANNERS_COPY } from './scanners.copy';

/**
 * The agency's own scanners (USR-16, UPL-13, **ADR-0098**).
 *
 * **A read-only screen, and most of its copy exists to say why.** An admin
 * arriving here looks for an Add and a Revoke and finds neither: issuing and
 * revoking a scanner credential are Blue Partner's acts in this release
 * (OPS-20), so a compromised token is a telephone call. A screen that simply
 * omitted the controls would read as broken rather than as deliberate, which is
 * what `managedBy` is for — and it names Blue Partner rather than "the
 * operator", because that is who an admin would ring.
 *
 * **It exists because the alternative is a credential nobody at the agency knows
 * about.** A scanner's service user is deliberately kept off the members roster
 * — it is not somebody an admin administers — and that is right, but it would
 * otherwise leave a machine writing into this agency's spaces with nowhere for
 * the agency to see it. The two `ingest.scanner_*` rows in the Audit log are the
 * other half of the same answer.
 *
 * **Not a surface**, on Passkeys' and General's grounds: `SURFACES` holds the
 * five things the product is *about*, and this is a short list an admin consults
 * occasionally rather than a place anybody works. `AGENCY_SETTINGS` in
 * `surfaces.ts` is where it joins the sidebar.
 *
 * **Nothing here can render a secret**, and the client has no field that could
 * carry one — the structural half of that, and the half worth keeping.
 */
export function ScannersSettings() {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'loaded'; scanners: Scanners }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    getScanners(controller.signal)
      .then((scanners) => {
        setState({ kind: 'loaded', scanners });
      })
      .catch((error: unknown) => {
        // An aborted request is a navigation, not a failure.
        if (controller.signal.aborted) return;
        setState({ kind: 'error', message: messageFrom(error, SCANNERS_COPY.failed) });
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((value) => value + 1);
  }, []);

  return (
    <SurfaceHeader title={SCANNERS_COPY.title} summary={SCANNERS_COPY.summary}>
      <p className="text-muted text-2xs mb-4">{SCANNERS_COPY.managedBy}</p>

      {state.kind === 'loading' ? (
        <p role="status" className="text-secondary text-sm">
          {SCANNERS_COPY.loading}
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div role="alert" className="flex flex-col items-start gap-3">
          <p className="text-sm">{state.message}</p>
          <button type="button" onClick={retry} className="text-accent text-sm underline">
            {SCANNERS_COPY.retry}
          </button>
        </div>
      ) : null}

      {state.kind === 'loaded' && state.scanners.scanners.length === 0 ? (
        <p className="text-secondary text-sm">{SCANNERS_COPY.empty}</p>
      ) : null}

      {state.kind === 'loaded' && state.scanners.scanners.length > 0 ? (
        <>
          <table aria-label={SCANNERS_COPY.table.label} className="w-full text-left text-sm">
            <thead className="text-muted text-2xs">
              <tr>
                <th scope="col" className="py-1 pr-3 font-medium">
                  {SCANNERS_COPY.table.scanner}
                </th>
                <th scope="col" className="py-1 pr-3 font-medium">
                  {SCANNERS_COPY.table.device}
                </th>
                <th scope="col" className="py-1 pr-3 font-medium">
                  {SCANNERS_COPY.table.uploadsAs}
                </th>
                <th scope="col" className="py-1 pr-3 font-medium">
                  {SCANNERS_COPY.table.destination}
                </th>
                <th scope="col" className="py-1 pr-3 font-medium">
                  {SCANNERS_COPY.table.lastUsed}
                </th>
                <th scope="col" className="py-1 font-medium">
                  {SCANNERS_COPY.table.status}
                </th>
              </tr>
            </thead>
            <tbody>
              {state.scanners.scanners.map((scanner) => (
                <tr key={scanner.scannerId} className="border-hairline border-t">
                  <td className="py-2 pr-3">{scanner.name}</td>
                  <td className="text-secondary py-2 pr-3">{scanner.deviceName}</td>
                  <td className="py-2 pr-3">{scanner.serviceUserName}</td>
                  <td className="py-2 pr-3">{scanner.spaceName}</td>
                  <td className="text-secondary py-2 pr-3">
                    {scanner.lastUsedAt ?? SCANNERS_COPY.neverUsed}
                  </td>
                  <td className="py-2">
                    <Badge>
                      {scanner.status === 'Active' ? SCANNERS_COPY.active : SCANNERS_COPY.revoked}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-muted text-2xs mt-4">{SCANNERS_COPY.seeAlso}</p>
        </>
      ) : null}
    </SurfaceHeader>
  );
}
